/**
 * ViewRedirectGuard
 *
 * Handles the smart redirect from /app to the correct home route,
 * based on the user's active view stored in ViewContextStore.
 *
 * This keeps redirect logic out of app.routes.ts (which cannot call
 * services) and in one testable, maintainable place.
 *
 *   /app  →  /app/carriers   (when activeView = 'carrier')
 *   /app  →  /app/shipper    (when activeView = 'shipper')
 *
 * Returns a UrlTree via router.createUrlTree() so Angular handles the
 * redirect SYNCHRONOUSLY within the same navigation cycle — no extra
 * router.navigate() call, no additional change-detection tick.
 */
import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { ViewContextStore } from '../../store/view-context.store';
import { PermissionService } from '../auth/permission.service';

export const viewRedirectGuard: CanActivateFn = (): UrlTree => {
  const router = inject(Router);
  const viewContextStore = inject(ViewContextStore);
  const permissionService = inject(PermissionService);

  // Redirect restricted shipper sub-profiles to their first allowed page
  // instead of the generic shipper home (which they cannot access).
  if (permissionService.isShipper()) {
    if (permissionService.isRequester()) {
      return router.createUrlTree(['/app/shipper/requests']);
    }
    if (permissionService.isOrder()) {
      return router.createUrlTree(['/app/shipper/orders']);
    }
  }

  return router.createUrlTree([viewContextStore.activeBaseRoute()]);
};
